/**
 * OIKE QUOTE DEFAULTS — V1
 * ------------------------------------------------------------
 * Textos predeterminados para las quote lines:
 * - shortDescription por servicio (lo que va en cada línea)
 * - términos comerciales por servicio + términos generales
 *
 * Editás el texto acá y se refleja en todas las propuestas nuevas.
 * Variables disponibles en los textos: {teamSize}, {countryName}, {validityDays}
 *
 * Los textos para clientes van en español neutro (sin voseo)
 * porque se envían a España, México, Chile, etc.
 */

export const QUOTE_DEFAULTS = {
  validityDays: 30,

  services: {
    diagnostic: {
      shortDescription: {
        es: "Análisis del sistema comercial actual para identificar cuellos de botella, causas raíz y prioridades. Incluye commercial baseline, diagnóstico y recomendaciones iniciales.",
        en: "Assessment of the current commercial system to identify bottlenecks, root causes and priorities. Includes commercial baseline, diagnosis and initial recommendations.",
      },
      duration: { es: "3 semanas (4 con 11 o más personas)", en: "3 weeks (4 for teams of 11 or more)" },
      paymentTerms: {
        es: "100% al inicio del proyecto.",
        en: "100% upfront at project start.",
      },
    },

    systemDesign: {
      shortDescription: {
        es: "Diseño del sistema comercial: proceso, funnel y etapas, ICP, estrategia de prospección, messaging framework, métricas, cadencias, roles y roadmap de implementación a 90 días.",
        en: "Commercial system design: process, funnel and stages, ICP, prospecting strategy, messaging framework, metrics, cadences, roles and a 90-day implementation roadmap.",
      },
      duration: { es: "4-6 semanas", en: "4-6 weeks" },
      paymentTerms: {
        es: "50% al inicio del proyecto y 50% a la entrega del sistema diseñado.",
        en: "50% at project start and 50% upon delivery of the designed system.",
      },
    },

    enablement: {
      shortDescription: {
        es: "Formación práctica para un equipo comercial de {teamSize} persona(s): workshops, role plays de prospección, cualificación y messaging, gestión de pipeline y adopción del sistema.",
        en: "Hands-on training for a commercial team of {teamSize}: workshops, prospecting, qualification and messaging role plays, pipeline management and system adoption.",
      },
      duration: { es: "3-4 semanas", en: "3-4 weeks" },
      paymentTerms: {
        es: "50% al inicio y 50% antes de la última sesión.",
        en: "50% at start and 50% before the final session.",
      },
    },

    advisory: {
      shortDescription: {
        es: "Retainer mensual de acompañamiento al founder / sales leader: revisión de métricas y pipeline, decisiones y prioridades comerciales, resolución de bloqueos y ajustes del sistema.",
        en: "Monthly advisory retainer for the founder / sales leader: metrics and pipeline review, commercial decisions and priorities, unblocking and system adjustments.",
      },
      duration: { es: "Mensual, compromiso mínimo de 3 meses", en: "Monthly, 3-month minimum commitment" },
      paymentTerms: {
        es: "Pago mensual por adelantado. Cancelable con 30 días de aviso después del compromiso mínimo.",
        en: "Monthly payment in advance. Cancellable with 30 days' notice after the minimum commitment.",
      },
      excludes: {
        es: "No incluye ejecución de prospección, administración diaria del CRM, gestión de vendedores ni responsabilidad directa sobre quota.",
        en: "Does not include prospecting execution, day-to-day CRM administration, managing sellers or direct quota responsibility.",
      },
    },
  },

  generalTerms: {
    es: [
      "Propuesta válida por {validityDays} días desde su fecha de emisión.",
      "Los precios no incluyen impuestos (IVA u otros aplicables según el país).",
      "El alcance y la inversión final se confirman tras la evaluación inicial.",
      "Oike diseña, forma y asesora. La ejecución de la operación comercial diaria es responsabilidad del cliente.",
      "El inicio del proyecto se agenda tras la aceptación de la propuesta y el primer pago.",
    ],
    en: [
      "This proposal is valid for {validityDays} days from its issue date.",
      "Prices exclude taxes (VAT or other applicable taxes depending on the country).",
      "Final scope and investment are confirmed after the initial assessment.",
      "Oike designs, enables and advises. Day-to-day execution of commercial operations remains the client's responsibility.",
      "Project start is scheduled after proposal acceptance and first payment.",
    ],
  },
};

// ============================================================
// HELPERS
// ============================================================
export function fillTemplate(text, vars = {}) {
  return String(text || "").replace(/\{(\w+)\}/g, (m, k) => (vars[k] ?? m));
}

/** Descripción corta de una línea de servicio */
export function getShortDescription(service, lang = "es", vars = {}) {
  const svc = QUOTE_DEFAULTS.services[service];
  if (!svc) return "";
  let text = fillTemplate(svc.shortDescription[lang], vars);
  if (svc.excludes) text += " " + svc.excludes[lang];
  return text;
}

/**
 * Términos comerciales completos para los servicios seleccionados.
 * Devuelve texto listo para el campo "commercial terms".
 */
export function buildCommercialTerms(services = [], lang = "es", vars = {}) {
  const v = { validityDays: QUOTE_DEFAULTS.validityDays, ...vars };
  const labels = lang === "es"
    ? { payment: "Forma de pago", duration: "Duración estimada", general: "Condiciones generales" }
    : { payment: "Payment terms", duration: "Estimated duration", general: "General terms" };
  const names = { diagnostic: "Diagnostic", systemDesign: "System Design", enablement: "Enablement", advisory: "Advisory" };

  const blocks = services
    .filter((s) => QUOTE_DEFAULTS.services[s])
    .map((s) => {
      const svc = QUOTE_DEFAULTS.services[s];
      return `Oike ${names[s]}\n- ${labels.duration}: ${svc.duration[lang]}\n- ${labels.payment}: ${svc.paymentTerms[lang]}`;
    });

  const general = QUOTE_DEFAULTS.generalTerms[lang].map((t) => `- ${fillTemplate(t, v)}`).join("\n");

  return [...blocks, `${labels.general}\n${general}`].join("\n\n");
}
